/**
 * First-touch signup attribution capture.
 *
 * On any page load, if the URL carries acquisition params (utm_*, gclid,
 * fbclid) or the visitor arrived from an external referrer, we snapshot
 * them ONCE into localStorage. After signup completes (email+password,
 * OAuth, or invite — all end with an authenticated session), the stored
 * snapshot is posted to `POST /auth/attribution`, which writes it onto the
 * user exactly once and flows it into their analytics identity.
 *
 * First-touch means first-touch: an existing snapshot is never overwritten
 * by a later campaign visit.
 */

const STORAGE_KEY = "arbi_signup_attribution";

const PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

const MAX_PARAM_LENGTH = 255;
const MAX_URL_LENGTH = 2000;

export type SignupAttribution = Partial<
  Record<(typeof PARAM_KEYS)[number] | "referrer" | "landing_page", string>
>;

function clamp(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function isExternalReferrer(referrer: string): boolean {
  try {
    return new URL(referrer).host !== window.location.host;
  } catch {
    return false;
  }
}

/** Snapshot acquisition params from the current URL (first touch only). */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const attribution: SignupAttribution = {};
    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) attribution[key] = clamp(value, MAX_PARAM_LENGTH);
    }

    const referrer = document.referrer;
    if (referrer && isExternalReferrer(referrer)) {
      attribution.referrer = clamp(referrer, MAX_URL_LENGTH);
    }

    // Only worth storing when there's an actual acquisition signal.
    if (Object.keys(attribution).length === 0) return;

    attribution.landing_page = clamp(window.location.href, MAX_URL_LENGTH);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Storage unavailable (private mode, quota) — attribution is
    // best-effort, never break the page for it.
  }
}

/** The stored snapshot, or null. */
export function getStoredAttribution(): SignupAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as SignupAttribution;
  } catch {
    return null;
  }
}

export function clearStoredAttribution(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
